const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

// Debug: Log API key status on module load
if (typeof window !== 'undefined') {
  console.log('=== Google Maps API Key Check ===');
  console.log('REACT_APP_GOOGLE_MAPS_API_KEY:', process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? '✓ Found' : '✗ Missing');
  console.log('REACT_APP_GOOGLE_PLACES_API_KEY:', process.env.REACT_APP_GOOGLE_PLACES_API_KEY ? '✓ Found' : '✗ Missing');
  console.log('Using API_KEY:', API_KEY ? `✓ ${API_KEY.substring(0, 10)}...` : '✗ NOT FOUND');
  console.log('================================');
}

let mapsLoaded = false;
let loadingPromise = null;

/**
 * Load Google Maps API using script tag approach (most reliable)
 */
const loadGoogleMapsScript = () => {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.google && window.google.maps) {
      resolve();
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
    if (existingScript) {
      console.log('Google Maps script already exists, waiting for it to load...');
      // Wait for existing script to load
      const checkInterval = setInterval(() => {
        if (window.google && window.google.maps) {
          console.log('Existing script loaded successfully');
          clearInterval(checkInterval);
          mapsLoaded = true;
          resolve();
        }
      }, 100);

      // Timeout after 30 seconds (longer for mobile networks)
      setTimeout(() => {
        clearInterval(checkInterval);
        if (!window.google || !window.google.maps) {
          console.error('Existing script timeout - Google Maps not loaded');
          reject(new Error('Google Maps script loading timeout. The script tag exists but Google Maps did not initialize. Check browser console for API errors.'));
        }
      }, 30000);
      return;
    }

    if (!API_KEY) {
      reject(new Error('Google Maps API key is not configured. Please set REACT_APP_GOOGLE_MAPS_API_KEY in your .env file'));
      return;
    }

    // Create a unique callback name to avoid conflicts
    const callbackName = `initGoogleMapsCallback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    console.log('Setting up Google Maps callback:', callbackName);
    console.log('API Key present:', !!API_KEY, 'Length:', API_KEY?.length);
    
    // Set up global callback BEFORE creating script
    window[callbackName] = function() {
      console.log('=== Google Maps callback FIRED ===', callbackName);
      console.log('window.google exists:', !!window.google);
      console.log('window.google.maps exists:', !!window.google?.maps);
      
      clearTimeout(timeoutId);
      
      // Clean up callback after a delay
      setTimeout(() => {
        if (window[callbackName]) {
          delete window[callbackName];
        }
      }, 1000);
      
      // Check if Google Maps loaded
      if (window.google && window.google.maps) {
        console.log('✓ Google Maps loaded successfully');
        mapsLoaded = true;
        resolve();
      } else {
        console.error('✗ Google Maps callback fired but window.google.maps is not available');
        console.error('window.google:', window.google);
        reject(new Error('Google Maps loaded but window.google.maps is not available. Check browser console for API errors.'));
      }
    };

    // Also set up a polling check as backup (in case callback doesn't fire)
    let pollCount = 0;
    const maxPolls = 300; // 30 seconds at 100ms intervals
    const pollInterval = setInterval(() => {
      pollCount++;
      if (window.google && window.google.maps) {
        console.log('Google Maps detected via polling (backup method)');
        clearInterval(pollInterval);
        clearTimeout(timeoutId);
        if (window[callbackName]) {
          delete window[callbackName];
        }
        mapsLoaded = true;
        resolve();
      } else if (pollCount >= maxPolls) {
        clearInterval(pollInterval);
        console.error('Polling timeout - Google Maps never loaded');
      }
    }, 100);

    // Create script tag with API key in URL
    const script = document.createElement('script');
    const scriptUrl = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=places,geocoding&callback=${callbackName}`;
    script.src = scriptUrl;
    script.async = true;
    script.defer = true;
    
    console.log('Creating script with URL:', scriptUrl.replace(API_KEY, 'API_KEY_HIDDEN'));
    
    // Add timeout for script loading (30 seconds for mobile networks)
    let timeoutId = setTimeout(() => {
      console.error('Google Maps script loading timeout after 30 seconds');
      clearInterval(pollInterval);
      script.remove();
      if (window[callbackName]) {
        delete window[callbackName];
      }
      reject(new Error('Google Maps script loading timeout. Check: 1) API key is valid, 2) API key has no restrictions, 3) Required APIs are enabled (Maps JavaScript API, Places API), 4) Browser console for errors.'));
    }, 30000);
    
    script.onerror = (error) => {
      console.error('Google Maps script onerror event:', error);
      console.error('Script src:', script.src.replace(API_KEY, 'API_KEY_HIDDEN'));
      clearTimeout(timeoutId);
      clearInterval(pollInterval);
      if (window[callbackName]) {
        delete window[callbackName];
      }
      reject(new Error('Failed to load Google Maps script. Check: 1) Internet connection, 2) API key is valid, 3) API key restrictions allow this domain/IP, 4) Browser console for CORS/network errors.'));
    };

    script.onload = () => {
      console.log('Google Maps script onload event fired (script downloaded)');
      console.log('Waiting for callback to fire...');
      // Script downloaded, but callback should fire separately
    };

    console.log('Appending script to document.head');
    try {
      document.head.appendChild(script);
      console.log('Script appended successfully');
    } catch (err) {
      console.error('Error appending to head, trying body:', err);
      document.body.appendChild(script);
    }
  });
};

/**
 * Initialize Google Maps API
 */
export const initGoogleMaps = async () => {
  // #region agent log
  try {
    fetch('http://127.0.0.1:7242/ingest/0061837b-d31b-4bc9-b253-97f40228a2c8',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'googleMaps.js:60',message:'initGoogleMaps called',data:{mapsLoaded,hasApiKey:!!API_KEY,apiKeyLength:API_KEY?.length},timestamp:Date.now(),sessionId:'debug-session',runId:'run5',hypothesisId:'T'})}).catch(()=>{});
  } catch(e) {}
  // #endregion

  if (mapsLoaded) {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/0061837b-d31b-4bc9-b253-97f40228a2c8',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'googleMaps.js:66',message:'Maps already loaded, returning',data:{},timestamp:Date.now(),sessionId:'debug-session',runId:'run5',hypothesisId:'T'})}).catch(()=>{});
    // #endregion
    return;
  }

  if (loadingPromise) {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/0061837b-d31b-4bc9-b253-97f40228a2c8',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'googleMaps.js:72',message:'Loading in progress, waiting',data:{},timestamp:Date.now(),sessionId:'debug-session',runId:'run5',hypothesisId:'T'})}).catch(()=>{});
    // #endregion
    return loadingPromise;
  }

  if (!API_KEY) {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/0061837b-d31b-4bc9-b253-97f40228a2c8',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'googleMaps.js:77',message:'No API key found',data:{},timestamp:Date.now(),sessionId:'debug-session',runId:'run5',hypothesisId:'T'})}).catch(()=>{});
    // #endregion
    throw new Error('Google Maps API key is not configured. Please set REACT_APP_GOOGLE_MAPS_API_KEY in your .env file');
  }

  // #region agent log
  fetch('http://127.0.0.1:7242/ingest/0061837b-d31b-4bc9-b253-97f40228a2c8',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'googleMaps.js:83',message:'Starting script load',data:{apiKeyPrefix:API_KEY.substring(0,15)},timestamp:Date.now(),sessionId:'debug-session',runId:'run5',hypothesisId:'U'})}).catch(()=>{});
  // #endregion

  loadingPromise = loadGoogleMapsScript()
    .then(() => {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/0061837b-d31b-4bc9-b253-97f40228a2c8',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'googleMaps.js:87',message:'Script loaded successfully',data:{hasGoogleMaps:!!window.google,hasMaps:!!window.google?.maps,hasPlaces:!!window.google?.maps?.places},timestamp:Date.now(),sessionId:'debug-session',runId:'run5',hypothesisId:'U'})}).catch(()=>{});
      // #endregion
      loadingPromise = null;
    })
    .catch((error) => {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/0061837b-d31b-4bc9-b253-97f40228a2c8',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'googleMaps.js:91',message:'Script load failed',data:{error:error.message},timestamp:Date.now(),sessionId:'debug-session',runId:'run5',hypothesisId:'U'})}).catch(()=>{});
      // #endregion
      mapsLoaded = false;
      loadingPromise = null;
      throw error;
    });

  return loadingPromise;
};

/**
 * Create autocomplete service for city/place search
 */
export const createAutocomplete = (inputElement, options = {}) => {
  if (!mapsLoaded && (!window.google || !window.google.maps)) {
    throw new Error('Google Maps API not loaded. Call initGoogleMaps() first.');
  }

  if (!window.google.maps.places) {
    throw new Error('Google Maps Places library not loaded.');
  }

  // Ensure input element is valid
  if (!inputElement || !inputElement.nodeName || inputElement.nodeName.toLowerCase() !== 'input') {
    throw new Error('Invalid input element for autocomplete');
  }

  const autocompleteOptions = {
    types: options.types || ['(cities)'],
    fields: ['place_id', 'name', 'geometry', 'formatted_address', 'address_components'],
    ...options,
  };

  // Remove fields from options if it was passed (to avoid duplication)
  if (options.fields) {
    autocompleteOptions.fields = options.fields;
  } else {
    // Ensure we always request the essential fields
    autocompleteOptions.fields = [
      'place_id',
      'name', 
      'geometry', 
      'formatted_address',
      ...(autocompleteOptions.fields || [])
    ];
    // Remove duplicates
    autocompleteOptions.fields = [...new Set(autocompleteOptions.fields)];
  }

  try {
    console.log('Creating autocomplete with options:', autocompleteOptions);
    console.log('Input element:', inputElement, 'Type:', inputElement.nodeName);
    
    const autocomplete = new window.google.maps.places.Autocomplete(inputElement, autocompleteOptions);
    
    console.log('Autocomplete instance created successfully');
    
    // Verify autocomplete is properly attached
    if (autocomplete && autocomplete.getPlace) {
      console.log('Autocomplete methods verified');
    }
    
    return autocomplete;
  } catch (error) {
    console.error('Error creating autocomplete:', error);
    console.error('Error stack:', error.stack);
    throw new Error(`Failed to create autocomplete: ${error.message}`);
  }
};

/**
 * Search for places using Places API
 */
export const searchPlaces = async (query, cityBounds = null) => {
  if (!mapsLoaded) {
    await initGoogleMaps();
  }

  const service = new window.google.maps.places.PlacesService(
    document.createElement('div')
  );

  return new Promise((resolve, reject) => {
    const request = {
      query: query,
      fields: [
        'place_id',
        'name',
        'geometry',
        'rating',
        'types',
        'photos',
        'formatted_address',
        'opening_hours',
      ],
    };

    if (cityBounds) {
      request.bounds = cityBounds;
    }

    service.textSearch(request, (results, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        resolve(results.map(place => ({
          placeId: place.place_id,
          name: place.name,
          coordinates: {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          },
          rating: place.rating || 0,
          types: place.types || [],
          photo: place.photos?.[0]?.getUrl({ maxWidth: 400 }) || null,
          address: place.formatted_address,
          openingHours: place.opening_hours,
        })));
      } else {
        reject(new Error(`Places search failed: ${status}`));
      }
    });
  });
};

/**
 * Get place details by place ID
 */
export const getPlaceDetails = async (placeId) => {
  if (!mapsLoaded) {
    await initGoogleMaps();
  }

  const service = new window.google.maps.places.PlacesService(
    document.createElement('div')
  );

  return new Promise((resolve, reject) => {
    service.getDetails(
      {
        placeId: placeId,
        fields: [
          'place_id',
          'name',
          'geometry',
          'rating',
          'types',
          'photos',
          'formatted_address',
          'opening_hours',
          'website',
          'international_phone_number',
        ],
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          resolve({
            placeId: place.place_id,
            name: place.name,
            coordinates: {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            },
            rating: place.rating || 0,
            types: place.types || [],
            photo: place.photos?.[0]?.getUrl({ maxWidth: 400 }) || null,
            address: place.formatted_address,
            openingHours: place.opening_hours,
            website: place.website,
            phone: place.international_phone_number,
          });
        } else {
          reject(new Error(`Place details failed: ${status}`));
        }
      }
    );
  });
};

/**
 * Calculate route between multiple points
 */
export const calculateRoute = async (waypoints, travelMode = 'WALKING', optimizeWaypoints = true) => {
  if (!mapsLoaded) {
    await initGoogleMaps();
  }

  const directionsService = new window.google.maps.DirectionsService();

  return new Promise((resolve, reject) => {
    const waypointsList = waypoints.slice(1, -1).map(wp => ({
      location: wp.coordinates,
      stopover: true,
    }));

    directionsService.route(
      {
        origin: waypoints[0].coordinates,
        destination: waypoints[waypoints.length - 1].coordinates,
        waypoints: waypointsList,
        travelMode: window.google.maps.TravelMode[travelMode],
        optimizeWaypoints: optimizeWaypoints, // Enable waypoint optimization for time-efficient routes
        // For walking routes, avoid highways and ferries
        avoidHighways: travelMode === 'WALKING',
        avoidFerries: travelMode === 'WALKING',
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          // Verify the route matches the requested travel mode
          const route = result.routes[0];
          if (route && route.legs) {
            const requestedMode = travelMode.toLowerCase();
            const actualModes = new Set();
            route.legs.forEach(leg => {
              leg.steps.forEach(step => {
                actualModes.add(step.travel_mode.toLowerCase());
              });
            });
            
            // If we requested walking but got other modes, log a warning
            if (requestedMode === 'walking' && !actualModes.has('walking') && actualModes.size > 0) {
              console.warn(`Route calculation returned ${Array.from(actualModes).join(', ')} instead of walking. This may indicate the route is too long for walking or no pedestrian path exists.`);
            }
          }
          resolve(result);
        } else {
          reject(new Error(`Directions failed: ${status}`));
        }
      }
    );
  });
};

/**
 * Get transit directions between two points
 */
export const getTransitDirections = async (origin, destination) => {
  if (!mapsLoaded) {
    await initGoogleMaps();
  }

  const directionsService = new window.google.maps.DirectionsService();

  return new Promise((resolve, reject) => {
    directionsService.route(
      {
        origin: origin,
        destination: destination,
        travelMode: window.google.maps.TravelMode.TRANSIT,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          const leg = result.routes[0].legs[0];
          resolve({
            duration: leg.duration.value / 60, // Convert to minutes
            distance: leg.distance.value / 1000, // Convert to km
            steps: leg.steps.map(step => ({
              instruction: step.instructions,
              duration: step.duration.value / 60,
              distance: step.distance.value / 1000,
              travelMode: step.travel_mode,
            })),
          });
        } else {
          reject(new Error(`Transit directions failed: ${status}`));
        }
      }
    );
  });
};

// Singleton AutocompleteService instance
let autocompleteService = null;

/**
 * Get place predictions using AutocompleteService
 * This allows rendering custom autocomplete UI instead of Google's dropdown
 * @param {string} input - Search query
 * @param {Object} options - Options for the autocomplete
 * @param {Object} options.location - Center point for bias { lat, lng }
 * @param {number} options.radius - Search radius in meters (default 50000)
 * @param {string[]} options.types - Place types to filter (e.g., ['establishment', 'geocode'])
 * @returns {Promise<Array>} - Array of place predictions
 */
export const getPlacePredictions = async (input, options = {}) => {
  if (!mapsLoaded) {
    await initGoogleMaps();
  }

  // Initialize AutocompleteService if needed
  if (!autocompleteService) {
    autocompleteService = new window.google.maps.places.AutocompleteService();
  }

  return new Promise((resolve, reject) => {
    if (!input || input.trim().length === 0) {
      resolve([]);
      return;
    }

    const request = {
      input: input.trim(),
      ...(options.location && {
        locationBias: {
          center: options.location,
          radius: options.radius || 50000,
        },
      }),
      ...(options.types && { types: options.types }),
    };

    autocompleteService.getPlacePredictions(request, (predictions, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
        const results = predictions.map(prediction => ({
          placeId: prediction.place_id,
          description: prediction.description,
          mainText: prediction.structured_formatting?.main_text || prediction.description,
          secondaryText: prediction.structured_formatting?.secondary_text || '',
          types: prediction.types || [],
        }));
        resolve(results);
      } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
        resolve([]);
      } else {
        // Don't reject on common non-error statuses
        if (status === window.google.maps.places.PlacesServiceStatus.INVALID_REQUEST) {
          resolve([]);
        } else {
          reject(new Error(`Place predictions failed: ${status}`));
        }
      }
    });
  });
};

/**
 * Get detailed place info from a prediction (for after selection)
 * @param {string} placeId - The place ID from a prediction
 * @returns {Promise<Object>} - Full place details including coordinates
 */
export const getPlaceFromPrediction = async (placeId) => {
  if (!mapsLoaded) {
    await initGoogleMaps();
  }

  const service = new window.google.maps.places.PlacesService(
    document.createElement('div')
  );

  return new Promise((resolve, reject) => {
    service.getDetails(
      {
        placeId: placeId,
        fields: [
          'place_id',
          'name',
          'geometry',
          'formatted_address',
          'types',
          'rating',
          'photos',
          'opening_hours',
        ],
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          resolve({
            placeId: place.place_id,
            name: place.name,
            coordinates: {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            },
            address: place.formatted_address,
            types: place.types || [],
            rating: place.rating || null,
            photo: place.photos?.[0]?.getUrl({ maxWidth: 400 }) || null,
            openingHours: place.opening_hours,
          });
        } else {
          reject(new Error(`Place details failed: ${status}`));
        }
      }
    );
  });
};
